import React, { useEffect, useState } from 'react';
import { TextField, IconButton, Box, Chip } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ILocation } from '../../types/global/helper';
import { showToastMessage } from '../../utils/AppUtils';

interface PlacesAutocompleteFieldProps {
    label: string;
    value: ILocation | ILocation[];
    onChange?: (value: ILocation | ILocation[]) => void;
    onSelect?: (value: ILocation | ILocation[]) => void;
    placeholder?: string;
    fullWidth?: boolean;
    multiple?: boolean;
}

export const PlacesAutocompleteField: React.FC<PlacesAutocompleteFieldProps> = ({
    label,
    value,
    onChange,
    placeholder = 'Search Places ...',
    fullWidth = true,
    multiple = false,
    onSelect,
}) => {
    const { themeStyle } = useThemeContext();
    const [textValue, setTextValue] = useState('');

    const handleLocationSelect = (address: string) => {
        // First update the address immediately
        setTextValue(address);

        // Then get the formatted address and coordinates
        geocodeByAddress(address)
            .then(results => {
                const immediateAddress = results[0].formatted_address;
                return getLatLng(results[0]).then(latLng => ({
                    latLng,
                    immediateAddress,
                }));
            })
            .then(({ latLng, immediateAddress }) => {
                if (latLng.lng && latLng.lat) {
                    const parsedPayload = {
                        type: 'Point',
                        coordinates: [latLng.lng, latLng.lat],
                        address: immediateAddress,
                        description: immediateAddress,
                        short_name: immediateAddress.split(',')[0],
                    };

                    if (multiple) {
                        const currentValues = Array.isArray(value) ? value : [];
                        onChange && onChange([...currentValues, parsedPayload]);
                        setTextValue(''); // Clear input after selection for multiple
                    } else {
                        onChange && onChange(parsedPayload);
                    }
                    onSelect && onSelect(parsedPayload);
                } else {
                    showToastMessage('No GeoKeys', 'warning');
                }
            })
            .catch(error => console.error('Error', error));
    };

    const handleRemoveLocation = (indexToRemove: number) => {
        if (multiple && Array.isArray(value)) {
            const newLocations = value.filter((_, index) => index !== indexToRemove);
            onChange && onChange(newLocations);
            onSelect && onSelect(newLocations);
        }
    };

    useEffect(() => {
        if (!multiple && value) {
            setTextValue((value as ILocation).address || '');
        } else if (multiple) {
            setTextValue('');
        }
    }, [value, multiple]);

    return (
        <Box>
            <PlacesAutocomplete
                value={textValue}
                onChange={value => {
                    if (!value.includes('•')) {
                        setTextValue(value);
                    }
                }}
                onSelect={handleLocationSelect}>
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div>
                        <TextField
                            fullWidth={fullWidth}
                            label={label}
                            placeholder={placeholder}
                            {...getInputProps()}
                            InputProps={{
                                endAdornment: textValue && (
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setTextValue('');
                                            if (!multiple) {
                                                onChange &&
                                                    onChange({
                                                        type: 'Point',
                                                        coordinates: [0, 0],
                                                        address: '',
                                                        description: '',
                                                        short_name: '',
                                                    });
                                            }
                                        }}
                                        sx={{
                                            color: theme[themeStyle].placeholderGray,
                                        }}>
                                        <CloseIcon />
                                    </IconButton>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: theme[themeStyle].tag_white,
                                    backgroundColor: theme[themeStyle].gray_text,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <div
                            className="autocomplete-dropdown-container"
                            style={{
                                position: 'absolute',
                                zIndex: 1000,
                                width: '100%',
                                maxWidth: '500px',
                            }}>
                            {loading && (
                                <div
                                    style={{
                                        backgroundColor: theme[themeStyle].gray_text,
                                        padding: '10px',
                                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        color: theme[themeStyle].primary_white,
                                    }}>
                                    Loading...
                                </div>
                            )}
                            {suggestions.map((suggestion, index) => {
                                const style = {
                                    backgroundColor: suggestion.active
                                        ? theme[themeStyle].background_gradient.end
                                        : theme[themeStyle].gray_text,
                                    padding: '10px',
                                    cursor: 'pointer',
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: theme[themeStyle].primary_white,
                                };
                                return (
                                    <div
                                        {...getSuggestionItemProps(suggestion, {
                                            style,
                                        })}
                                        key={index}>
                                        <span>{suggestion.description}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </PlacesAutocomplete>
            {multiple && Array.isArray(value) && value.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {value.map((location, index) => (
                        <Chip
                            key={index}
                            label={location.address}
                            onDelete={() => handleRemoveLocation(index)}
                            sx={{
                                backgroundColor: theme[themeStyle].background_gradient.end,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                '& .MuiChip-deleteIcon': {
                                    color: theme[themeStyle].primary_white,
                                    '&:hover': {
                                        color: theme[themeStyle].primary_orange,
                                    },
                                },
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
